import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Department } from "@/lib/auth-context";
import type { Submission } from "@/lib/submissions";

export interface AdminProfile {
  id: string;
  email: string;
  full_name: string;
  avatar_url: string | null;
  employee_id: string | null;
  department: Department;
  monthly_target: number;
  status: string;
  is_active?: boolean;
  deactivated_at?: string | null;
  deactivated_by?: string | null;
  created_at: string;
  moderator_permissions?: Record<string, unknown> | null;
  can_add_vault_entries?: boolean;
  mfa_enabled?: boolean;
}

export function useAllProfiles() {
  return useQuery({
    queryKey: ["admin", "profiles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .order("full_name");
      if (error) throw error;
      return (data ?? []) as unknown as AdminProfile[];
    },
    staleTime: 30_000,
  });
}

/**
 * Active-only variant of useAllProfiles for NEW-SELECTION pickers.
 * Use useAllProfiles for ID→name resolvers, historical renders, and the admin Users tab.
 */
export function useActiveProfiles() {
  return useQuery({
    queryKey: ["admin", "profiles", "active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("status", "active")
        .order("full_name");
      if (error) throw error;
      return (data ?? []) as unknown as AdminProfile[];
    },
    staleTime: 30_000,
  });
}

export interface UserRoleRow {
  user_id: string;
  role: "admin" | "moderator" | "user";
}

export function useAllRoles() {
  return useQuery({
    queryKey: ["admin", "roles"],
    queryFn: async () => {
      const { data, error } = await supabase.from("user_roles").select("user_id, role");
      if (error) throw error;
      return (data ?? []) as UserRoleRow[];
    },
    staleTime: 30_000,
  });
}

export function useAllSubmissions() {
  return useQuery({
    queryKey: ["admin", "submissions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("submissions")
        .select("*")
        .order("submission_date", { ascending: false })
        .limit(1000);
      if (error) throw error;
      return (data ?? []) as unknown as Submission[];
    },
    staleTime: 30_000,
  });
}

export interface AuditRow {
  id: string;
  action_type: string;
  admin_id: string | null;
  target_user_id: string | null;
  before_data: unknown;
  after_data: unknown;
  created_at: string;
}

export function useAuditLog() {
  return useQuery({
    queryKey: ["admin", "audit"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("audit_log")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as AuditRow[];
    },
    staleTime: 15_000,
  });
}

export interface BrandRow {
  id: string;
  name: string;
  active: boolean;
}
export interface ProductRow {
  id: string;
  brand: string;
  name: string;
  active: boolean;
}

export function useAllBrands() {
  return useQuery({
    queryKey: ["admin", "brands"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("brands")
        .select("*")
        .order("name");
      if (error) throw error;
      return (data ?? []) as BrandRow[];
    },
    staleTime: 60_000,
  });
}

export function useAllProducts() {
  return useQuery({
    queryKey: ["admin", "products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .order("brand")
        .order("name");
      if (error) throw error;
      return (data ?? []) as ProductRow[];
    },
    staleTime: 60_000,
  });
}
